import type { QueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { applyStreamEvent } from "./apply";
import { Election } from "./election";
import { getConnectionStatus, setConnectionStatus } from "./status";
import { Stream } from "./stream";
import type { LiveEvent, LiveStatus } from "./types";

/**
 * Live sync for this browser (plan B4), started by the authenticated layout.
 *
 * Order of things, and why it is safe:
 *   1. `GET /events/head` is read before anything loads lite rows (the layout awaits `startLive`
 *      for a moment), so every row fetched afterwards already includes everything up to `head`.
 *   2. The leader tab opens `EventSource(/api/events?since=<seq>)`; the server replays what
 *      happened after that and then streams. Events at or below the last applied seq are dropped,
 *      and store.ts drops rows that are not newer by version, so replays and overlaps are harmless.
 *   3. The leader rebroadcasts every event on the BroadcastChannel; each tab applies it to its own
 *      cache. A tab that takes over resumes from the last seq it saw.
 *   4. `resync` (access changed, retention) invalidates every cached query; a 410 when
 *      (re)connecting additionally starts again from a fresh head.
 */

type Wire = { t: "ev"; ev: LiveEvent } | { t: "reset"; seq: number };
type HeartbeatState = { status: LiveStatus; seq: number };

interface Session {
  qc: QueryClient;
  userId: string;
  election: Election;
  stream: Stream;
  lastSeq: number;
  /** The head is known: events are applied instead of buffered. */
  headKnown: boolean;
  buffer: LiveEvent[];
  ready: Promise<void>;
  stopped: boolean;
  dispose: () => void;
}

let session: Session | null = null;

/** How long the layout waits for the head before rendering anyway. */
const GATE_MS = 1200;

const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

/** Starts (or keeps) live sync for `userId`. Resolves once the head is known, or after a short grace period. */
export function startLive(qc: QueryClient, userId: string): Promise<void> {
  if (session && !session.stopped && session.userId === userId && session.qc === qc) return gate(session);
  stopLive();
  session = createSession(qc, userId);
  return gate(session);
}

export function stopLive() {
  const s = session;
  session = null;
  if (!s) return;
  s.stopped = true;
  s.dispose();
  s.election.stop();
  s.stream.close();
  setConnectionStatus("connecting");
}

const gate = (s: Session) => Promise.race([s.ready, sleep(GATE_MS)]);

async function fetchHead(s: Session): Promise<number> {
  for (let attempt = 0; !s.stopped; attempt++) {
    try {
      const { seq } = await api.get<{ seq: number }>("/api/v1/events/head");
      return seq;
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        stopLive();
        break;
      }
      await sleep(Math.min(1000 * 2 ** attempt, 15_000));
    }
  }
  return -1;
}

function createSession(qc: QueryClient, userId: string): Session {
  const s: Session = {
    qc,
    userId,
    lastSeq: 0,
    headKnown: false,
    buffer: [],
    stopped: false,
    ready: Promise.resolve(),
    dispose: () => {},
    election: undefined as unknown as Election,
    stream: undefined as unknown as Stream,
  };

  const apply = (ev: LiveEvent) => {
    try {
      applyStreamEvent(ev, { qc, meId: s.userId });
    } catch (err) {
      console.error("[live] could not apply event", ev.kind, err);
    }
  };

  const handle = (ev: LiveEvent) => {
    if (!s.headKnown) {
      s.buffer.push(ev);
      return;
    }
    // `resync` carries no new position (and must never be skipped); everything else is ordered by seq.
    if (ev.kind !== "resync") {
      if (ev.seq <= s.lastSeq) return;
      s.lastSeq = ev.seq;
    }
    apply(ev);
  };

  const reset = (seq: number) => {
    s.lastSeq = seq;
    void qc.invalidateQueries();
  };

  s.stream = new Stream({
    since: () => s.lastSeq,
    onEvent: (ev) => {
      s.election.broadcast({ t: "ev", ev } satisfies Wire);
      handle(ev);
    },
    onStatus: (status) => {
      setConnectionStatus(status);
      s.election.pulse();
    },
    onGone: () => {
      void (async () => {
        const head = await fetchHead(s);
        if (head < 0 || s.stopped) return;
        s.election.broadcast({ t: "reset", seq: head } satisfies Wire);
        reset(head);
        if (s.election.isLeader) s.stream.open();
      })();
    },
    onUnauthorized: () => {
      // The session ended; the query layer sends the user to /login, which tears this down.
      s.stream.close();
      setConnectionStatus("connecting");
    },
  });

  s.election = new Election({
    onLead: () => {
      void s.ready.then(() => {
        if (!s.stopped && s.election.isLeader && s.headKnown) s.stream.open();
      });
    },
    onStepDown: () => s.stream.close(),
    onData: (data) => {
      const msg = data as Wire;
      if (msg.t === "ev") handle(msg.ev);
      else if (msg.t === "reset" && s.headKnown) reset(msg.seq);
    },
    onLeaderState: (state) => {
      const st = state as HeartbeatState | undefined;
      if (!st) return;
      setConnectionStatus(st.status);
      // Everything the leader had applied was broadcast before this heartbeat (the channel is ordered).
      if (s.headKnown && st.seq > s.lastSeq) s.lastSeq = st.seq;
    },
    heartbeatState: (): HeartbeatState => ({ status: getConnectionStatus(), seq: s.lastSeq }),
  });

  const online = () => {
    if (s.election.isLeader) s.stream.reconnectNow();
  };
  window.addEventListener("online", online);

  // Sign-out (or the 401 handler) clears the cache: the connection belongs to the old user.
  const unsubscribe = qc.getQueryCache().subscribe((event) => {
    if (event.type === "removed" && event.query.queryKey[0] === "me") stopLive();
  });
  s.dispose = () => {
    window.removeEventListener("online", online);
    unsubscribe();
  };

  // Listen before the head is known so nothing the leader broadcasts meanwhile is lost.
  s.election.start();

  const started = Date.now();
  s.ready = (async () => {
    const head = await fetchHead(s);
    if (head < 0 || s.stopped) return;
    s.lastSeq = Math.max(s.lastSeq, head);
    s.headKnown = true;
    const queued = s.buffer;
    s.buffer = [];
    for (const ev of queued) handle(ev);
    // Rows fetched while the head was still unknown may miss changes between their snapshot and the head.
    if (Date.now() - started > GATE_MS) void qc.invalidateQueries({ queryKey: ["lites"] });
  })();
  return s;
}
